/**
 * Record Manager
 * 
 * Responsible for managing record operations and transformations
 */

import { executeTransaction } from './indexedDBUtils';
import { ensureValidTimestamp, normalizeTimestamp } from './dbStorageHelper';
import { ensureRecordHasUuid, validateRecordUuid, updateLastModified, prepareRecordForSync, handleRecordDependencies } from '../../utils/uuidHelper';

class RecordManager {
    /**
     * Helper method to manually check for existing SystemConfig records
     */
    async manualSystemConfigCheck(store, record, currentSyncStartTimeDT) {
        return new Promise((resolve, reject) => {
            // Get all records and manually find matching category and name
            const request = store.getAll();
            
            request.onsuccess = (event) => {
                const allRecords = event.target.result;
                const existingRecord = allRecords.find(r => 
                    r.category === record.category && r.name === record.name
                );
                
                if (existingRecord) {
                    // Update the existing record with new data but keep its uuid
                    const updatedRecord = {
                        ...existingRecord,
                        ...record,
                        uuid: existingRecord.uuid, // Keep the original uuid
                        lastModified: currentSyncStartTimeDT
                    };
                    store.put(updatedRecord);
                } else {
                    // No existing record, add the new one
                    const newRecord = {
                        ...record,
                        lastModified: currentSyncStartTimeDT
                    };
                    store.put(newRecord);
                }
                resolve();
            };
            
            request.onerror = (error) => {
                console.error('Error getting all SystemConfig records:', error);
                // Still try to add the record
                const newRecord = {
                    ...record,
                    lastModified: currentSyncStartTimeDT
                };
                store.put(newRecord);
                resolve();
            };
        });
    }

    /**
     * Gets records modified since the given timestamp
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table to query
     * @param {Date|string} timestamp - Timestamp to check against
     * @param {Object} options - Additional options for the query
     * @returns {Promise<Array>} Array of matching records
     */
    async getRecordsModifiedSince(db, tableName, timestamp, options = {}) {
        const { limit = 100, offset = 0, fields = null } = options;
        
        try {
            // Ensure the table exists
            if (!db.objectStoreNames.contains(tableName)) {
                console.debug(`[dbStorageService] Table ${tableName} doesn't exist when getting modified records`);
                return [];
            }
            
            // Normalize and validate the timestamp
            const validTimestamp = ensureValidTimestamp(
                timestamp, 
                true, 
                `getRecordsModifiedSince:${tableName}`
            );
            
            // Convert to ISO string for string comparison
            const validTimestampString = normalizeTimestamp(validTimestamp);
            // console.log(`[dbStorageService TIMESTAMP DEBUG] Looking for records in ${tableName} modified after: ${validTimestampString}`);
            
            // Use the transaction wrapper for proper lifecycle management
            return executeTransaction(db, tableName, (store) => {
                if (!store.indexNames.contains('lastModified')) {
                    console.warn(`[dbStorageService] Table ${tableName} missing lastModified index`);
                    return Promise.resolve([]);
                }
                
                // Use ">" rather than ">=" to ensure we only get records AFTER the timestamp
                // This is critical to prevent infinite sync loops
                const index = store.index('lastModified');
                
                // Use lowerBound with exclusive flag (false = exclusive)
                // This ensures we only get records with lastModified > validTimestamp, not >= 
                const range = IDBKeyRange.lowerBound(validTimestamp, false); // exclusive
                
                return new Promise((resolve) => {
                    const results = [];
                    let skipped = 0;
                    
                    const cursorRequest = index.openCursor(range);
                    
                    cursorRequest.onsuccess = (event) => {
                        const cursor = event.target.result;
                        
                        if (!cursor) {
                            // No more records
                            // console.log(`[dbStorageService TIMESTAMP DEBUG] Found ${results.length} modified records in ${tableName}`);
                            return resolve(results);
                        }
                        
                        // Double-check the timestamp is actually greater than our reference
                        // This provides an extra safety check beyond the IndexedDB range
                        if (cursor.value.lastModified <= validTimestampString) {
                            // Skip this record and continue
                            cursor.continue();
                            return;
                        }
                        
                        // Handle offset
                        if (skipped < offset) {
                            skipped++;
                            cursor.continue();
                            return;
                        }
                        
                        // Check limit
                        if (limit > 0 && results.length >= limit) {
                            return resolve(results);
                        }
                        
                        // If specific fields are requested, only include those
                        if (fields) {
                            const record = {};
                            fields.forEach(field => {
                                if (cursor.value[field] !== undefined) {
                                    record[field] = cursor.value[field];
                                }
                            });
                            results.push(record);
                        } else {
                            // Include all fields
                            results.push(cursor.value);
                        }
                        
                        cursor.continue();
                    };
                    
                    cursorRequest.onerror = (event) => {
                        console.error(`[dbStorageService] Error getting modified records for ${tableName}:`, event.target.error);
                        resolve([]); // Return empty array on error
                    };
                });
            });
        } catch (error) {
            console.error(`[dbStorageService] Error getting records modified since for ${tableName}:`, error);
            return []; // Return empty array
        }
    }

    /**
     * Checks if a table has any records modified since the given timestamp
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table to check
     * @param {Date|string} timestamp - Timestamp to check against
     * @returns {Promise<boolean>} Whether the table has changes
     */
    async hasChangedRecordsSince(db, tableName, timestamp) {
        // Early validation - if no timestamp, definitely need to sync
        if (timestamp === null || timestamp === undefined) {
            console.debug(`[dbStorageService] No timestamp provided for ${tableName}, assuming changes exist`);
            return true;
        }
        
        try {
            // Ensure the table exists
            if (!db.objectStoreNames.contains(tableName)) {
                console.debug(`[dbStorageService] Table ${tableName} doesn't exist, no changes possible`);
                return false;
            }
            
            // Normalize and validate the timestamp
            const validTimestamp = ensureValidTimestamp(
                timestamp, 
                true, 
                `hasChangedRecordsSince:${tableName}`
            );
            
            // Convert to ISO string for consistent comparison
            const validTimestampString = normalizeTimestamp(validTimestamp);
            // console.log(`[dbStorageService TIMESTAMP DEBUG] Checking for changes in ${tableName} after: ${validTimestampString}`);
            
            // Use the transaction wrapper for proper lifecycle management
            return executeTransaction(db, tableName, (store) => {
                // Ensure the lastModified index exists
                if (!store.indexNames.contains('lastModified')) {
                    console.warn(`[dbStorageService] Table ${tableName} missing lastModified index, assuming changes exist`);
                    return Promise.resolve(true);
                }
                
                const index = store.index('lastModified');
                // Use exclusive range (false parameter) for strict "greater than" comparison
                const range = IDBKeyRange.lowerBound(validTimestamp, false); // Exclusive
                
                return new Promise((resolve) => {
                    // Use count() for better performance than getting all records
                    const request = index.count(range);
                    
                    request.onsuccess = () => {
                        const count = request.result;
                        // console.log(`[dbStorageService TIMESTAMP DEBUG] ${tableName} has ${count} changed records since ${validTimestampString}`);
                        resolve(count > 0);
                    };
                    
                    request.onerror = (event) => {
                        console.error(`[dbStorageService] Error checking for changes in ${tableName}:`, event.target.error);
                        resolve(true); // Assume changes exist on error
                    };
                });
            });
        } catch (error) {
            console.error(`[dbStorageService] Critical error in hasChangedRecordsSince for ${tableName}:`, error);
            return true; // Assume changes exist on any error
        }
    }

    /**
     * Get records that have been changed after a given timestamp
     * Uses optimized query that limits fields returned
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - Name of the table to query
     * @param {Date|string} timestamp - Timestamp to check changes since
     * @param {Object} options - Optional parameters
     * @returns {Promise<Array>} - Array of changed records
     */
    async getChangedRecordsSince(db, tableName, timestamp, options = {}) {
        const { limit = 100, offset = 0, fields = ['uuid', 'lastModified'] } = options;
        const compareDate = timestamp instanceof Date ? timestamp : new Date(timestamp);
        
        // Ensure we have a valid date
        if (isNaN(compareDate.getTime())) {
            console.warn(`Invalid timestamp for change detection: ${timestamp}`);
            return []; // Return empty array for invalid timestamp
        }
        
        try {
            return new Promise((resolve, reject) => {
                const txn = db.transaction(tableName, 'readonly');
                const store = txn.objectStore(tableName);
                
                // Use the lastModified index for efficient querying
                if (!store.indexNames.contains('lastModified')) {
                    console.warn(`Table ${tableName} does not have a lastModified index`);
                    resolve([]); // Return empty array if index doesn't exist
                    return;
                }
                
                const index = store.index('lastModified');
                const range = IDBKeyRange.lowerBound(compareDate.toISOString(), false);
                const request = index.openCursor(range);
                
                const changedRecords = [];
                let skipped = 0;
                
                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    
                    if (!cursor) {
                        // No more records
                        resolve(changedRecords);
                        return;
                    }
                    
                    // Handle offset (skip records)
                    if (skipped < offset) {
                        skipped++;
                        cursor.continue();
                        return;
                    }
                    
                    // Check if we've reached the limit
                    if (changedRecords.length >= limit) {
                        resolve(changedRecords);
                        return;
                    }
                    
                    // If fields is null or undefined, return the complete record
                    if (fields === null || fields === undefined) {
                        changedRecords.push(cursor.value);
                    } else {
                        // Extract only the requested fields for efficiency
                        const record = {};
                        fields.forEach(field => {
                            if (cursor.value[field] !== undefined) {
                                record[field] = cursor.value[field];
                            }
                        });
                        changedRecords.push(record);
                    }
                    
                    cursor.continue();
                };
                
                request.onerror = (event) => {
                    console.error(`Error getting changed records in ${tableName}:`, event.target.error);
                    reject(event.target.error);
                };
            });
        } catch (error) {
            console.error(`Error getting changed records in ${tableName}:`, error);
            throw error;
        }
    }

    /**
     * Process a batch of updates for a specific table
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - The name of the table
     * @param {Array} updates - Array of update objects
     * @returns {Promise<number>} - Number of successfully processed updates
     */
    async processBatchUpdates(db, tableName, updates) {
        if (!updates || !updates.length) {
            return 0;
        }
        
        // console.log(`[DbStorageService] Processing batch of ${updates.length} updates for ${tableName}`);
        
        // Start a transaction for the batch
        return executeTransaction(db, tableName, async (store) => {
            let successCount = 0;
            
            // Process each update in the batch within the same transaction
            for (const update of updates) {
                try {
                    if (update.action === 'DELETE') {
                        await store.delete(update.id);
                    } else if (update.action === 'UPDATE' || update.action === 'INSERT') {
                        // Add sync metadata to the record
                        const record = {
                            ...update.data,
                            _syncedAt: Date.now(),
                            _syncStatus: 'synced'
                        };
                        
                        await store.put(record);
                    }
                    
                    successCount++;
                } catch (error) {
                    console.error(`[DbStorageService] Error processing update for ${tableName}:`, error, update);
                    // Continue with the rest of the batch even if one update fails
                }
            }
            
            return successCount;
        }, 'readwrite');
    }

    /**
     * Cleans up records marked as deleted after they have been synchronized with the server
     * @param {IDBDatabase} db - Database instance
     * @param {string} tableName - The name of the table to clean up
     * @param {Object} options - Options for cleanup
     * @returns {Promise<Object>} - Results of the cleanup operation
     */
    async cleanupSyncedDeletes(db, tableName, options = {}) {
        const { 
            onlySuccessfullySynced = true, 
            batchSize = 100
        } = options;
        
        // console.log(`[dbStorageService] Starting cleanup of deleted records in table ${tableName}`);
        
        // Ensure the table exists
        if (!db.objectStoreNames.contains(tableName)) {
            // console.log(`[dbStorageService] Table ${tableName} doesn't exist, nothing to clean up`);
            return { 
                success: true, 
                tableName, 
                deletedCount: 0, 
                message: 'Table does not exist' 
            };
        }
        
        try {
            // Get records marked as deleted
            const deletedRecords = await this.getRecordsByIndex(db, tableName, 'isDeleted', true);
            
            if (!deletedRecords || deletedRecords.length === 0) {
                // console.log(`[dbStorageService] No deleted records found in table ${tableName}`);
                return { 
                    success: true, 
                    tableName, 
                    deletedCount: 0, 
                    message: 'No deleted records found' 
                };
            }
            
            // console.log(`[dbStorageService] Found ${deletedRecords.length} deleted records in table ${tableName}`);
            
            // Add safety check for synced records if required
            let recordsToDelete = deletedRecords;
            
            if (onlySuccessfullySynced) {
                // Get the last sync time
                const lastSyncTime = localStorage.getItem('lastSyncTime');
                
                if (!lastSyncTime) {
                    console.warn(`[dbStorageService] No last sync time found, skipping cleanup for safety`);
                    return { 
                        success: false, 
                        tableName, 
                        deletedCount: 0, 
                        message: 'No last sync time found, skipping cleanup for safety' 
                    };
                }
                
                // Only delete records that were modified before the last sync
                // This ensures they have been sent to the server
                recordsToDelete = deletedRecords.filter(record => {
                    // If record has no lastModified, consider it not synced for safety
                    if (!record.lastModified) return false;
                    
                    return new Date(record.lastModified) <= new Date(lastSyncTime);
                });
                
                // console.log(`[dbStorageService] ${recordsToDelete.length} of ${deletedRecords.length} deleted records are safe to remove (synced with server)`);
            }
            
            if (recordsToDelete.length === 0) {
                return { 
                    success: true, 
                    tableName, 
                    deletedCount: 0, 
                    message: 'No records ready for deletion' 
                };
            }
            
            // Process in batches to avoid transaction timeouts
            const batches = [];
            for (let i = 0; i < recordsToDelete.length; i += batchSize) {
                batches.push(recordsToDelete.slice(i, i + batchSize));
            }
            
            // console.log(`[dbStorageService] Processing ${batches.length} batches of deleted records`);
            
            let totalDeleted = 0;
            
            for (let i = 0; i < batches.length; i++) {
                const batch = batches[i];
                // console.log(`[dbStorageService] Processing batch ${i + 1}/${batches.length} (${batch.length} records)`);
                
                await executeTransaction(db, tableName, async (store) => {
                    for (const record of batch) {
                        const request = store.delete(record.uuid);
                        await new Promise((resolve, reject) => {
                            request.onsuccess = resolve;
                            request.onerror = (e) => reject(e.target.error);
                        });
                    }
                }, 'readwrite');
                
                totalDeleted += batch.length;
                
                // Small delay between batches to prevent blocking UI
                if (i < batches.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 5));
                }
            }
            
            // console.log(`[dbStorageService] Successfully removed ${totalDeleted} deleted records from table ${tableName}`);
            
            return {
                success: true,
                tableName,
                deletedCount: totalDeleted,
                message: `Successfully removed ${totalDeleted} deleted records`
            };
        } catch (error) {
            console.error(`[dbStorageService] Error cleaning up deleted records in ${tableName}:`, error);
            return {
                success: false,
                tableName,
                deletedCount: 0,
                error: error.message || String(error),
                message: `Error cleaning up deleted records: ${error.message || String(error)}`
            };
        }
    }

    async getRecordsByIndex(db, tableName, indexName, value) {
        // Check if the table exists before attempting a transaction
        if (!db || !db.objectStoreNames.contains(tableName)) {
            console.warn(`[dbStorageService] Table ${tableName} doesn't exist yet, returning empty result for index query`);
            return [];
        }
        
        try {
            const transaction = db.transaction(tableName, 'readonly');
            const store = transaction.objectStore(tableName);
            
            if (!store.indexNames.contains(indexName)) {
                console.warn(`[dbStorageService] Index ${indexName} not found in table ${tableName}, falling back to filter`);
                // If the index doesn't exist, fall back to getting all records and filtering
                const allRecords = await new Promise((resolve, reject) => {
                    const request = store.getAll();
                    request.onsuccess = () => resolve(request.result);
                    request.onerror = (e) => reject(e.target.error);
                });
                
                // Filter manually
                return allRecords.filter(record => record[indexName] === value);
            }
            
            const index = store.index(indexName);
            
            // Handle special case for boolean values (commonly used for isDeleted)
            if (typeof value === 'boolean') {
                // Use getAll() without parameters and filter the results
                const allIndexRecords = await new Promise((resolve, reject) => {
                    const request = index.getAll();
                    request.onsuccess = () => resolve(request.result);
                    request.onerror = (e) => reject(e.target.error);
                });
                
                // Filter for the specific boolean value
                return allIndexRecords.filter(record => record[indexName] === value);
            } else {
                // For non-boolean values, use direct getAll with the value
                return new Promise((resolve, reject) => {
                    const request = index.getAll(value);
                    request.onsuccess = () => resolve(request.result);
                    request.onerror = (e) => reject(e.target.error);
                });
            }
        } catch (error) {
            console.error(`[dbStorageService] Error in getRecordsByIndex for ${tableName}.${indexName}:`, error);
            // Fall back to using getRecordsByField which has more robust error handling
            return this.getRecordsByField(db, tableName, indexName, value);
        }
    }

    async getRecordsByField(db, tableName, fieldName, fieldValue) {
        // Check if the table exists before attempting a transaction
        if (!db || !db.objectStoreNames.contains(tableName)) {
            console.warn(`[dbStorageService] Table ${tableName} doesn't exist yet, returning empty result for field query`);
            return [];
        }
        
        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction(tableName, 'readonly');
                const store = transaction.objectStore(tableName);

                let request;
                if (store.indexNames.contains(fieldName)) {
                    const index = store.index(fieldName);
                    request = index.getAll(fieldValue);
                } else {
                    request = store.getAll();
                }

                request.onsuccess = () => {
                    let results = request.result;
                    if (!store.indexNames.contains(fieldName)) {
                        results = results.filter(record => record[fieldName] === fieldValue);
                    }
                    resolve(results);
                };

                request.onerror = () => {
                    console.error('Error fetching records:', request.error);
                    reject(request.error);
                };
            } catch (error) {
                console.error('Transaction error:', error);
                reject(error);
            }
        });
    }
}

export default new RecordManager(); 